import {
  collectSlotOccurrences,
  groupSlotOccurrences,
} from "@core/components/telegram/model/slots"
import type { MessageContent } from "@core/components/telegram/types"
import type {
  Binding,
  Bindings,
  NamedId,
  ReadBindings,
} from "../interface/publications"
import { isExternalId } from "./ids"

const record = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value)

const namedId = (value: unknown): value is NamedId =>
  record(value) &&
  Object.keys(value).every((key) => key === "id" || key === "name") &&
  isExternalId(value.id) &&
  typeof value.name === "string" &&
  value.name.trim().length > 0

/** Validate without normalizing saved metadata, URLs, IDs or user edits. */
export function isBinding(value: unknown): value is Binding {
  if (!record(value)) return false
  if ("url" in value) {
    if (
      Object.keys(value).length !== 1 ||
      typeof value.url !== "string" ||
      /\s/.test(value.url)
    )
      return false
    try {
      const url = new URL(value.url)
      return (
        /^https?:\/\//i.test(value.url) &&
        (url.protocol === "http:" || url.protocol === "https:") &&
        !!url.hostname
      )
    } catch {
      return false
    }
  }
  return (
    Object.keys(value).every((key) =>
      ["bot", "botType", "referral"].includes(key),
    ) &&
    namedId(value.bot) &&
    /^[A-Za-z][A-Za-z0-9_]{4,31}$/.test(value.bot.name) &&
    (value.botType === undefined || namedId(value.botType)) &&
    (value.referral === undefined ||
      (namedId(value.referral) &&
        /^[A-Za-z0-9_-]{1,64}$/.test(value.referral.name)))
  )
}

export function assertBinding(value: unknown): asserts value is Binding {
  if (!isBinding(value))
    throw new TypeError(
      "Binding requires an HTTP(S) URL or complete named bot metadata",
    )
}

/** Explicit write shape: optional undefined form values are omitted, never null.
 * Cloning avoids sharing mutable form metadata with an in-flight request. */
export function toBindingWrite(value: unknown): Binding {
  assertBinding(value)
  if (value.url !== undefined) return { url: value.url }
  return {
    bot: { id: value.bot.id, name: value.bot.name },
    ...(value.botType === undefined
      ? {}
      : { botType: { id: value.botType.id, name: value.botType.name } }),
    ...(value.referral === undefined
      ? {}
      : { referral: { id: value.referral.id, name: value.referral.name } }),
  }
}

/** Legacy IDs have no resolvable name here; never invent one or use a REF ID. */
export function bindingUrl(
  value: ReadBindings[string] | undefined,
): string | undefined {
  if (!isBinding(value)) return undefined
  if (value.url !== undefined) return value.url
  return `https://t.me/${value.bot.name}${value.referral ? `?start=${value.referral.name}` : ""}`
}

export const getActiveBindingSlots = (content: MessageContent) =>
  groupSlotOccurrences(collectSlotOccurrences(content))

/** Presence is separate from write validation: an unchanged legacy read is retained. */
export const getMissingActiveBindings = (
  content: MessageContent,
  bindings: ReadBindings,
) =>
  getActiveBindingSlots(content).filter(
    ({ slotId }) =>
      !Object.prototype.hasOwnProperty.call(bindings, slotId) ||
      bindings[slotId] == null,
  )

/** PATCH semantics: omitted UUIDs (including inactive/legacy) survive; values replace. */
export function mergeBindings(
  saved: ReadBindings,
  replacements: Bindings,
): ReadBindings {
  return {
    ...saved,
    ...Object.fromEntries(
      Object.entries(replacements).map(([id, value]) => [
        id,
        toBindingWrite(value),
      ]),
    ),
  }
}
